import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from 'react-icons/md';
import { IoMdAdd } from 'react-icons/io';
import { Layout, Header } from '../../../../components';
import { handleApi, formatMediumDate, capitalize } from '../../../../utils/utils';
import AppContext from '../../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { userData, currentColor, setSuppressAutoNavigate } = useContext(AppContext);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const dropdownRef = useRef(null);
  useEffect(() => {
    const timeout = setTimeout(() => fetchCustomers(), 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => setCurrentPage(1), [searchTerm]);

  const fetchCustomers = async () => {
    const apiEndpoint = 'Customers';
    setIsLoading(true);
    try {
      const response = await handleApi(apiEndpoint, {}, userData, 'GET', {});
      if (response.error) {
        toast.error(`Error: ${response.errorMessage}`);
        return;
      }
      console.log('response data sir',response.data)
      setCustomers(response.data);

    } catch (error) {
      toast.error(error.message || 'An unexpected error occurred.');
    } finally {setIsLoading(false);}
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const debouncedSearchTerm = useMemo(() => debounce(handleSearchChange, 300), []);

  const filteredCustomers = useMemo(() => {
    return customers.filter((cat) =>
      cat?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [customers, searchTerm]);

  const sortedFilteredCustomers = filteredCustomers.sort(
    (a, b) => new Date(b.create_time) - new Date(a.create_time)
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = sortedFilteredCustomers.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handleClosePage = () => {
    navigate(-1); 
  };


  const handleAction = (action, customer) => {
    setSuppressAutoNavigate(true); // prevent auto redirect
    switch (action) {
      case 'new':
        navigate(`/customers/new`, { state: { customers } }); // pass customers
  
        break;
      case 'view':
        navigate(`/customers/view/${customer.id}`, { state: { initialCustomer: customer } });
        break;
      case 'edit':
        navigate(`/customers/edit/${customer.id}`,  {state: { initialCustomer:customer, customers } });
      
        break;
      case 'approve':
        navigate(`/customers/approve/${customer.id}`, { state: { customer: customer } });
        break;
      default:toast.error(`Action "${action}" is not supported.`);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-white text-white  p-4 sm:p-8">
        {/* Header and Close Button */}
                  <div className="relative flex justify-between items-center">
                    <h1 className=" font-extrabold text-gray-800 tracking-wide flex-grow text-center">
                      CUSTOMERS
                    </h1>
                    <button
                      onClick={handleClosePage}
                      className="p-2 rounded-full text-gray-800 bg-gray-200 hover:bg-gray-300 transition-all duration-300 "
                      style={{ color: currentColor }}
                    >
                      <MdOutlineCancel size={30} />
                    </button>
                  </div>
        <ToastContainer position="top-center" autoClose={2000} />
        
        {isLoading ? (
          <div className="flex items-center flex-col py-6">
            <Audio height="80" width="80" radius="9" color={currentColor} ariaLabel="loading" />
            <p className="mt-2 text-white">Loading. Please wait...</p>
          </div>
        ) : (
         
          <div className="bg-gray-500 p-4 sm:p-6  rounded-3xl  border border-gray-300 mx-auto max-w-[60%]">
            {/* Search Input */}
            <div className="mb-2 flex justify-between">
              <div className="relative w-full sm:w-1/2">
                {/* Emoji on the Left */}
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg text-gray-500 select-none">🔍</span>

                {/* Input Field */}
                <input
                  type="text"
                  placeholder="Search customer"
                  onChange={debouncedSearchTerm}
                  className="w-full py-2.5 pl-10 pr-4 rounded-xl bg-gray-200 text-gray-800 placeholder-gray-500 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white transition duration-300 text-sm sm:text-base"
                />
              </div>
              {/* add new button */}
              <button
                className="text-white px-2 py-2 text-sm font-semibold rounded-lg flex items-center gap-1 bg-blue-500 hover:bg-blue-700 transition-all duration-300"
                onClick={() => handleAction('new', {})}
              >
                <IoMdAdd size={20} />
                <span>New</span>
              </button>
            </div>

            {/* Pagination */}
             {/* Pagination */}
             <div className="flex flex-wrap justify-center gap-1 mb-1 ">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => paginate(index + 1)}
                    className={`px-2  text-sm rounded-full font-semibold transition-colors duration-300
                               ${currentPage === index + 1 ? 'bg-cyan-500 text-white ' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

            {/* Table Display */}
            {/* Category Cards Display */}
                          <div className="space-y-2">
                            {currentItems.length > 0 ? (
                              currentItems.map((item) => (
                                <div key={item.id} className="relative">
                                  <div
                                    onClick={() => setSelectedRowId(item.id === selectedRowId ? null : item.id)}
                                    className="flex items-center gap-4 mx-5 px-2 py-1  hover:bg-gray-700 transition-all duration-200 
                                               cursor-pointer rounded-2xl  border border-gray-100"
                                  >
                                    <div className="flex-shrink-0 flex flex-col items-center">
  {/* Customer Image */}
  <img
    className="rounded-full h-8 w-8 object-cover ring-2 ring-gray-700"
    src={
      item?.photo_file_name
        ? `http://dev.neon-mw.com:3001/api/customer_categories/photos/${item.photo_file_name}`
        : `https://placehold.co/100x100/1f2937/e5e7eb?text=${
            item?.name
              ?.split(' ')
              .map(word => word.charAt(0))
              .join('')
              .toUpperCase() || 'NP'
          }`
    }
    alt={`${item?.name || 'Category'} image`}
    onError={(e) => {
      e.target.onerror = null;
      e.target.src = `https://placehold.co/100x100/1f2937/e5e7eb?text=${
        item?.name
          ?.split(' ')
          .map(word => word.charAt(0))
          .join('')
          .toUpperCase() || 'NP'
      }`;
    }}
  />

  {/* Category label */}
  <span className="mt-1 text-[8px] text-gray-200 font-medium truncate max-w-[60px]" title={item?.districts?.name || 'N/A'}>
    {item?.districts?.name || 'N/A'}
  </span>
</div>

                                    <div className="flex-grow text-white text-sm leading-tight">
  {/* Customer name + price in one row */}
  <div className="flex items-center justify-between">
    <span className="mt-1 text-[13px] text-white font-medium " title={capitalize(item?.name) || 'N/A'}>
      {capitalize(item?.name) || 'N/A'}
    </span>
   
  </div>

  {/* Second row just for category + stock */}
  <div className="text-xs text-gray-300 flex flex-wrap gap-x-2">

    <span> {item?.phone_number || '0'} <span className="font-bold ml-2">|</span> <span className=" text-xs  text-gray-5500">
       {item?.email || 'N/A'}
    </span></span>
  </div>
</div>

                                    <div className="text-white">
                                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 transform transition-transform duration-300 ${selectedRowId === item.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </div>
                                  </div>
                                  
                                  {selectedRowId === item.id && (
                                    <div
                                      ref={dropdownRef}
                                      className="absolute z-10 right-4 top-16 bg-[var(--currentColor)] backdrop-blur-md 
                                                 ring-1 ring-cyan-500/30 border border-gray-300 rounded-xl p-2 w-48 
                                                 flex flex-col animate-fade-in-down"
                                    >
                                      {item.approved === 1 ? (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedRowId(null);
                                            handleAction('view', item);
                                          }}
                                          className="text-left px-4 py-2 text-sm text-white hover:bg-white/20 hover:text-yellow-300 transition duration-300 rounded-lg"
                                        >
                                          👁️ View
                                        </button>
                                      ) : (
                                        <>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedRowId(null);
                                              handleAction('edit', item);
                                            }}
                                            className="text-left px-4 py-2 text-sm text-white hover:bg-white/20 hover:text-blue-300 transition duration-300 rounded-t-lg"
                                          >
                                            ✏️ Edit
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedRowId(null);
                                              handleAction('view', item);
                                            }}
                                            className="text-left px-4 py-2 text-sm text-white hover:bg-white/20 hover:text-yellow-300 transition duration-300"
                                          >
                                            👁️ View
                                          </button>
                                          <div className="border-t border-white/20 my-1" />
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedRowId(null);
                                              handleAction('deactivate', item);
                                            }}
                                            className="text-left px-4 py-2 text-sm text-white hover:bg-white/20 hover:text-green-300 transition duration-300 rounded-b-lg"
                                          >
                                          ❌ Deactivate
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="text-center text-white/50 py-10">
                                <p className="text-lg">No categories found.</p>
                              </div>
                            )}
                          </div>
            
            {console.log('Current Items for display sir :', currentItems)}
                    
          </div>
        )}
      </div>
    </Layout>
  );
};
export default Customers;